import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Smartphone, Laptop, Tablet, Watch, Package, Wrench, Phone, ShoppingCart, UserCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const LogoSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 280 56" fill="none" className="h-9">
    {/* Outer ambient halo */}
    <circle cx="28" cy="28" r="27.5" fill="#FF5C00" opacity="0.05"/>

    {/* Tool elements radiating outside the ring — simplified tool/component shapes */}
    <rect x="26" y="0.5" width="4" height="4.5" rx="0.8" fill="#383840"/>
    <rect x="40" y="3.5" width="4" height="4.5" rx="0.8" fill="#383840" transform="rotate(45 42 5.75)"/>
    <rect x="49.5" y="26" width="4.5" height="4" rx="0.8" fill="#383840"/>
    <rect x="43" y="43.5" width="4" height="4.5" rx="0.8" fill="#383840" transform="rotate(-45 45 45.75)"/>
    <rect x="26" y="51" width="4" height="4.5" rx="0.8" fill="#383840"/>
    <rect x="10" y="43.5" width="4" height="4.5" rx="0.8" fill="#383840" transform="rotate(45 12 45.75)"/>
    <rect x="2" y="26" width="4.5" height="4" rx="0.8" fill="#383840"/>
    <rect x="10" y="3.5" width="4" height="4.5" rx="0.8" fill="#383840" transform="rotate(-45 12 5.75)"/>

    {/* Extra tool shapes at mid-angles for density */}
    <line x1="46" y1="12" x2="49.5" y2="9" stroke="#383840" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="10" y1="12" x2="6.5" y2="9" stroke="#383840" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="46" y1="44" x2="49.5" y2="47" stroke="#383840" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="10" y1="44" x2="6.5" y2="47" stroke="#383840" strokeWidth="2.5" strokeLinecap="round"/>

    {/* Dark mechanical ring border — the porthole frame */}
    <circle cx="28" cy="28" r="22.5" stroke="#252528" strokeWidth="6" fill="none"/>
    {/* Ring inner edge highlight */}
    <circle cx="28" cy="28" r="19.5" stroke="rgba(255,255,255,0.07)" strokeWidth="0.5" fill="none"/>

    {/* Orange main circle */}
    <circle cx="28" cy="28" r="19" fill="#FF5C00"/>

    {/* Vertical stripe texture — the ribbed panel effect */}
    <line x1="18" y1="9.5" x2="18" y2="46.5" stroke="#000" strokeWidth="1.5" opacity="0.13"/>
    <line x1="21.5" y1="9.5" x2="21.5" y2="46.5" stroke="#000" strokeWidth="1.5" opacity="0.13"/>
    <line x1="25" y1="9.5" x2="25" y2="46.5" stroke="#000" strokeWidth="1.5" opacity="0.13"/>
    <line x1="28.5" y1="9.5" x2="28.5" y2="46.5" stroke="#000" strokeWidth="1.5" opacity="0.13"/>
    <line x1="32" y1="9.5" x2="32" y2="46.5" stroke="#000" strokeWidth="1.5" opacity="0.13"/>
    <line x1="35.5" y1="9.5" x2="35.5" y2="46.5" stroke="#000" strokeWidth="1.5" opacity="0.13"/>
    <line x1="39" y1="9.5" x2="39" y2="46.5" stroke="#000" strokeWidth="1.5" opacity="0.13"/>

    {/* Apple silhouette — darker orange tone against the lighter orange, matching reference */}
    <g transform="translate(13, 10) scale(0.8)" fill="#000" opacity="0.32">
      <path d="M28.6 22.3c0-4.5 3.8-7.3 3.9-7.4-2.1-3-5.3-3.5-6.5-3.5-2.8-.3-5.4 1.6-6.8 1.6-1.4 0-3.6-1.5-5.9-1.5-3 0-5.8 1.8-7.3 4.4-3.1 5.3-.8 13.2 2.2 17.5 1.5 2.1 3.2 4.4 5.5 4.3 2.2-.1 3-1.4 5.7-1.4 2.6 0 3.4 1.4 5.7 1.4 2.3 0 3.8-2.1 5.2-4.2 1.7-2.4 2.4-4.8 2.5-4.9-.1-.1-4.2-1.6-4.2-6.3zm-3.6-10.1c1.2-1.5 2.1-3.6 1.8-5.6-1.8.1-4 1.2-5.2 2.7-1.1 1.3-2.1 3.4-1.9 5.4 2 .2 3.9-.9 5.3-2.5z"/>
    </g>

    {/* Brand name */}
    <text x="62" y="37" fontFamily="Cabinet Grotesk, sans-serif" fontSize="22" fontWeight="800" fill="#F5F5F7" letterSpacing="-0.5">Atelier du <tspan fill="#FF5C00">Mac</tspan></text>
  </svg>
);

const megaMenus = {
  reparation: {
    label: "Réparation",
    items: [
      { icon: Smartphone, label: "iPhone", desc: "Tous modèles iPhone 6 à 16", path: "/reparation/iphone" },
      { icon: Laptop, label: "Mac", desc: "MacBook Air, Pro, iMac, Mac Mini", path: "/reparation/mac" },
      { icon: Tablet, label: "iPad", desc: "iPad, Air, Pro, mini", path: "/reparation/ipad" },
      { icon: Watch, label: "Apple Watch", desc: "Series 1 à 9, SE, Ultra", path: "/reparation/apple-watch" },
    ]
  },
  services: {
    label: "Services",
    items: [
      { icon: Package, label: "Accessoires", desc: "Chargeurs, câbles, coques, adaptateurs", path: "/accessoires" },
      { icon: Wrench, label: "Diagnostic", desc: "Diagnostic gratuit", path: "/contact" },
    ]
  }
};

export default function Navbar() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count, setIsOpen } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-2xl bg-[#0A0A0C]/90 border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex-shrink-0">
          <img
            src="/logo-atelierdumac.png"
            alt="Atelier du Mac"
            className="h-10 w-auto object-contain"
            style={{ maxWidth: '180px' }}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {Object.entries(megaMenus).map(([key, menu]) => (
            <div
              key={key}
              className="relative"
              onMouseEnter={() => setOpenMenu(key)}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button
                data-testid={`nav-${key}`}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/5"
              >
                {menu.label}
                <ChevronDown className={`w-4 h-4 transition-transform ${openMenu === key ? 'rotate-180' : ''}`} />
              </button>

              {openMenu === key && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                  <div className="glass rounded-2xl p-3 w-72 shadow-2xl shadow-black/50">
                    {menu.items.map((item) => (
                      <Link
                        key={item.path + item.label}
                        to={item.path}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group"
                        data-testid={`nav-${key}-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-[#FF5C00]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#FF5C00]/20 transition-colors">
                          <item.icon className="w-4 h-4 text-[#FF5C00]" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white">{item.label}</div>
                          <div className="text-xs text-white/50">{item.desc}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          <Link to="/devis" data-testid="nav-devis" className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/5">
            Devis
          </Link>
          <Link to="/nos-magasins" data-testid="nav-magasins" className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/5">
            Nos ateliers
          </Link>
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Cart icon */}
          <button
            data-testid="navbar-cart-btn"
            onClick={() => setIsOpen(true)}
            className="relative p-2 text-white/60 hover:text-white transition-colors"
          >
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#FF5C00] text-white text-[9px] font-bold flex items-center justify-center">
                {count > 9 ? "9+" : count}
              </span>
            )}
          </button>
          {/* Mon compte */}
          {user ? (
            <Link to="/profil" data-testid="navbar-profile-btn"
              className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
              {user.picture
                ? <img src={user.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                : <UserCircle className="w-4 h-4" />}
              {user.prenom}
            </Link>
          ) : (
            <Link to="/connexion" data-testid="navbar-login-btn"
              className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
              <UserCircle className="w-4 h-4" /> Mon compte
            </Link>
          )}
          <Link
            to="/contact"
            data-testid="nav-contact-btn"
            className="flex items-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 hover:-translate-y-0.5"
          >
            <Phone className="w-4 h-4" />
            Prendre RDV
          </Link>
        </div>

        {/* Mobile — icônes directement visibles sans ouvrir le menu */}
        <div className="lg:hidden flex items-center gap-1">
          {/* Compte */}
          {user ? (
            <Link to="/profil" data-testid="navbar-profile-mobile-btn"
              className="relative p-2 text-white/60 hover:text-white transition-colors flex items-center gap-1.5">
              {user.picture
                ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                : <UserCircle className="w-5 h-5" />}
              <span className="text-xs font-semibold text-white/70 max-w-[60px] truncate">{user.prenom}</span>
            </Link>
          ) : (
            <Link to="/connexion" data-testid="navbar-login-mobile-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white/70 text-xs font-semibold hover:border-white/30 transition-colors">
              <UserCircle className="w-4 h-4" />
              Mon compte
            </Link>
          )}
          {/* Panier */}
          <button
            data-testid="navbar-cart-mobile-btn"
            onClick={() => setIsOpen(true)}
            className="relative p-2 text-white/60 hover:text-white"
          >
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#FF5C00] text-white text-[9px] font-bold flex items-center justify-center">
                {count > 9 ? "9+" : count}
              </span>
            )}
          </button>
          {/* Hamburger */}
          <button
            data-testid="navbar-mobile-toggle"
            className="p-2 text-white/70 hover:text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden glass border-t border-white/10"
        >
          <div className="px-6 py-4 space-y-1">
            <p className="text-xs font-bold uppercase tracking-widest text-white/30 px-3 pb-2">Réparation</p>
            {megaMenus.reparation.items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 transition-colors"
                data-testid={`mobile-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-5 h-5 text-[#FF5C00]" />
                <span className="text-white font-medium">{item.label}</span>
              </Link>
            ))}
            <div className="border-t border-white/10 my-2" />
            <Link to="/devis" className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-white font-medium" data-testid="mobile-devis">
              <Wrench className="w-5 h-5 text-[#FF5C00]" />
              Devis en ligne
            </Link>
            <Link to="/nos-magasins" className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-white font-medium" data-testid="mobile-magasins">
              <Package className="w-5 h-5 text-[#FF5C00]" />
              Nos ateliers
            </Link>
            <div className="border-t border-white/10 my-2" />
            {user ? (
              <Link to="/profil" onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-white font-medium"
                data-testid="mobile-profil-btn">
                <UserCircle className="w-5 h-5 text-[#FF5C00]" />
                Mon profil ({user.prenom})
              </Link>
            ) : (
              <>
                <Link to="/connexion" onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/5 text-white font-medium"
                  data-testid="mobile-connexion-btn">
                  <UserCircle className="w-5 h-5 text-[#FF5C00]" />
                  Se connecter
                </Link>
                <Link to="/connexion?mode=register" onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium"
                  data-testid="mobile-register-btn">
                  <UserCircle className="w-5 h-5 text-[#FF5C00]" />
                  Créer un compte
                </Link>
              </>
            )}
            <Link
              to="/contact"
              className="block mt-3 bg-[#FF5C00] text-white text-center font-semibold py-3 rounded-full"
              data-testid="mobile-contact-btn"
            >
              Prendre RDV
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
